import { useEffect, useMemo, useState } from 'react';
import { C } from '../../theme';
import { QBR_METRICS } from '../../qbr/grading';
import { getActiveQbrThresholds } from '../../db';
import { fmxFetch } from '../../apiClient';

// Step 1 of the QBR flow.
//
// Two decisions live here:
//   1. The reporting window (quarter). We default to the most recently
//      completed calendar quarter so the FMXer rarely has to touch this.
//   2. Whether to use the admin-managed standard thresholds for grading,
//      or a per-run override. In override mode the thresholds are editable
//      inline; whatever the user saves becomes the snapshot stored on
//      qbr_runs.thresholds_used so the run is reproducible.
//
// Output (via onReady): { quarterLabel, periodStart, periodEnd, thresholds }
// — passed to the next step which will pull FMX data and grade against
// these thresholds.
export default function QbrSetupStep({ project, onContinue }) {
  const quarterOptions = useMemo(() => recentQuarters(8), []);
  const [quarterKey, setQuarterKey] = useState(quarterOptions[0].key);
  const selected = quarterOptions.find(q => q.key === quarterKey) || quarterOptions[0];
  const [thresholdMode, setThresholdMode] = useState('standard'); // 'standard' | 'custom'

  const [standardThresholds, setStandardThresholds] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [overrides, setOverrides] = useState({}); // { [metric]: configJson }

  // Module selection — every workRequestModules + workTaskModules
  // entry on the project gets a checkbox, all checked by default.
  // Unchecking excludes the module from the fetcher's scope list for
  // this QBR run. Lets the FMXer scope a quarterly report to (e.g.)
  // just Maintenance when a customer also has separate IT or Auto
  // modules that aren't relevant this period.
  const workRequestModules = project?.fmx_modules?.workRequestModules || [];
  const workTaskModules    = project?.fmx_modules?.workTaskModules    || [];
  const [selectedWRSlugs, setSelectedWRSlugs] = useState(() => new Set(workRequestModules.map(m => m.slug)));
  const [selectedWTSlugs, setSelectedWTSlugs] = useState(() => new Set(workTaskModules.map(m => m.slug)));

  function toggleSlug(setFn, set, slug) {
    const next = new Set(set);
    if (next.has(slug)) next.delete(slug); else next.add(slug);
    setFn(next);
  }

  // FMX user types — drive the "power user" selection for the
  // adoption_power metric. Default to all-checked; FMXer unchecks
  // user types that shouldn't count as power users (e.g. read-only
  // viewers, public requestors).
  const [userTypes, setUserTypes] = useState([]);
  const [userTypesLoading, setUserTypesLoading] = useState(false);
  const [userTypesError,   setUserTypesError]   = useState(null);
  const [powerUserTypeIds, setPowerUserTypeIds] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    if (!project?.id || !project?.fmx_connection_verified) return;
    setUserTypesLoading(true);
    fmxFetch({ projectId: project.id, endpoint: '/v1/user-types?limit=200', method: 'GET' })
      .then(async res => {
        if (!res.ok) throw new Error(`/v1/user-types returned ${res.status}`);
        const body = await res.json();
        const items = Array.isArray(body) ? body : (body?.items || body?.data || []);
        return items.filter(t => !t.isDeleted).map(t => ({ id: t.id, name: t.name || `Type ${t.id}` }));
      })
      .then(list => {
        if (cancelled) return;
        setUserTypes(list);
        setPowerUserTypeIds(new Set(list.map(t => t.id)));
      })
      .catch(e => { if (!cancelled) setUserTypesError(e.message); })
      .finally(() => { if (!cancelled) setUserTypesLoading(false); });
    return () => { cancelled = true; };
  }, [project?.id, project?.fmx_connection_verified]);

  function toggleUserType(id) {
    setPowerUserTypeIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  useEffect(() => {
    let cancelled = false;
    getActiveQbrThresholds().then(byMetric => {
      if (cancelled) return;
      if (!byMetric || Object.keys(byMetric).length === 0) {
        setLoadError('No grading thresholds found. Have migrations 024 + 025 been applied?');
        return;
      }
      setStandardThresholds(byMetric);
      setOverrides(byMetric);
    });
    return () => { cancelled = true; };
  }, []);

  // At least one module needs to be selected — otherwise the Data
  // step has nothing to pull. Skip this check on projects that have
  // no modules configured at all (the checklist UI is hidden anyway).
  const anyModule = workRequestModules.length > 0 || workTaskModules.length > 0;
  const moduleSelectionOk = !anyModule || (selectedWRSlugs.size > 0 || selectedWTSlugs.size > 0);
  const ready = standardThresholds && selected && moduleSelectionOk;

  function handleContinue() {
    const thresholds = thresholdMode === 'custom' ? overrides : standardThresholds;
    onContinue({
      quarterLabel: selected.label,
      periodStart: selected.periodStart,
      periodEnd: selected.periodEnd,
      thresholds,
      thresholdMode,
      // Per-run subset of modules to pull from. Empty arrays would
      // mean 'don't pull anything' downstream — guarded against in
      // Data step so an FMXer can't accidentally skip everything.
      selectedWRSlugs: Array.from(selectedWRSlugs),
      selectedWTSlugs: Array.from(selectedWTSlugs),
      // User types that count toward the adoption_power metric. Empty
      // array = no power-user filter (the metric stays null and the
      // card is hidden).
      powerUserTypeIds: Array.from(powerUserTypeIds),
    });
  }

  return (
    <div>
      <Section title="Reporting period">
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Field label="Quarter">
            <select
              value={quarterKey}
              onChange={e => setQuarterKey(e.target.value)}
              style={{ ...input, minWidth: 160 }}
            >
              {quarterOptions.map(q => (
                <option key={q.key} value={q.key}>
                  {q.label}{q.isCurrent ? ' (in progress)' : ''}
                </option>
              ))}
            </select>
          </Field>
          <div style={{
            padding: '6px 10px', borderRadius: 6,
            background: C.navyTint, color: C.navy,
            fontSize: 12, fontWeight: 500,
          }}>
            {formatRange(selected.periodStart, selected.periodEnd)}
          </div>
        </div>
      </Section>

      {(workRequestModules.length > 0 || workTaskModules.length > 0) && (
        <Section title="Modules to include">
          <div style={{ fontSize: 12, color: C.textMid, marginBottom: 8 }}>
            All modules on this project are pulled by default. Uncheck any you don't want included in this QBR run.
          </div>
          <ModuleChecklist
            label="Work-request modules"
            modules={workRequestModules}
            selected={selectedWRSlugs}
            onToggle={slug => toggleSlug(setSelectedWRSlugs, selectedWRSlugs, slug)}
          />
          <ModuleChecklist
            label="Work-task (PM) modules"
            modules={workTaskModules}
            selected={selectedWTSlugs}
            onToggle={slug => toggleSlug(setSelectedWTSlugs, selectedWTSlugs, slug)}
          />
          {selectedWRSlugs.size === 0 && selectedWTSlugs.size === 0 && (
            <div style={{
              padding: '8px 12px', borderRadius: 5, fontSize: 11, marginTop: 6,
              background: '#FEF2F2', color: '#991B1B', border: '1px solid #FECACA',
            }}>
              Every module is unchecked — the Data step will have nothing to pull. Re-check at least one to proceed.
            </div>
          )}
        </Section>
      )}

      <Section title="Power-user types">
        <div style={{ fontSize: 12, color: C.textMid, marginBottom: 8 }}>
          Drives the <strong>Power-User Adoption</strong> metric. By default every FMX user type counts;
          uncheck any types you'd rather exclude (e.g. read-only viewers or public-portal requestors).
          Adoption-overall stays calculated across the full eligible user base.
        </div>
        {userTypesLoading && <div style={{ fontSize: 12, color: C.textLight }}>Loading user types…</div>}
        {userTypesError && (
          <div style={{
            padding: '8px 12px', borderRadius: 5, fontSize: 11,
            background: '#FFFBEB', color: '#92400E', border: '1px solid #FCD34D',
          }}>
            Couldn't load user types: {userTypesError}. Power-user metric will be skipped.
          </div>
        )}
        {!userTypesLoading && userTypes.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {userTypes.map(t => {
              const on = powerUserTypeIds.has(t.id);
              return (
                <label key={t.id} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '5px 10px', fontSize: 12,
                  border: `1px solid ${on ? C.orange : C.border}`,
                  background: on ? '#FFF6F0' : '#fff',
                  borderRadius: 5, cursor: 'pointer',
                }}>
                  <input type="checkbox" checked={on} onChange={() => toggleUserType(t.id)} />
                  <span style={{ fontWeight: 600, color: C.navy }}>{t.name}</span>
                </label>
              );
            })}
          </div>
        )}
      </Section>

      <Section title="Grading thresholds">
        <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
          <Radio
            checked={thresholdMode === 'standard'}
            onChange={() => setThresholdMode('standard')}
            label="Use standard thresholds"
            sublabel="Admin-managed rubric applied to every QBR."
          />
          <Radio
            checked={thresholdMode === 'custom'}
            onChange={() => setThresholdMode('custom')}
            label="Use custom thresholds for this QBR"
            sublabel="Edit the cutoffs below. The snapshot is saved with the run."
          />
        </div>

        {loadError && <div style={errBox}>{loadError}</div>}

        {standardThresholds && (
          <ThresholdGrid
            thresholds={thresholdMode === 'custom' ? overrides : standardThresholds}
            editable={thresholdMode === 'custom'}
            onChange={overrides => setOverrides(overrides)}
          />
        )}
      </Section>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
        <button
          onClick={handleContinue} disabled={!ready}
          style={{
            padding: '10px 24px', fontSize: 14, fontWeight: 600,
            background: ready ? C.orange : '#D1D5DB', color: '#fff',
            border: 'none', borderRadius: 6,
            cursor: ready ? 'pointer' : 'not-allowed',
          }}
        >Continue →</button>
      </div>
    </div>
  );
}

function ThresholdGrid({ thresholds, editable, onChange }) {
  function setTier(metric, tierIdx, key, raw) {
    if (!editable) return;
    const next = { ...thresholds };
    const cfg = { ...next[metric] };
    const tiers = (cfg.tiers || []).map(t => ({ ...t }));
    tiers[tierIdx] = { ...tiers[tierIdx], [key]: raw === '' ? null : Number(raw) };
    cfg.tiers = tiers;
    next[metric] = cfg;
    onChange(next);
  }

  return (
    <div style={{
      display: 'grid', gap: 12,
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    }}>
      {QBR_METRICS.map(m => {
        const cfg = thresholds[m.key];
        if (!cfg) return null;
        const tierKeys = numericTierKeys(cfg.tiers);
        return (
          <div key={m.key} style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ fontWeight: 600, color: C.navy }}>{m.label}</div>
              <div style={{ fontSize: 11, color: C.textMid }}>{cfg.description}</div>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={tinyTh}>Grade</th>
                  {tierKeys.map(k => <th key={k} style={tinyTh}>{prettyKey(k)}</th>)}
                </tr>
              </thead>
              <tbody>
                {(cfg.tiers || []).map((tier, idx) => (
                  <tr key={tier.grade}>
                    <td style={{ ...tinyTd, fontWeight: 600 }}>{tier.grade}</td>
                    {tierKeys.map(k => (
                      <td key={k} style={tinyTd}>
                        <input
                          type="number"
                          value={tier[k] ?? ''}
                          disabled={!editable}
                          onChange={e => setTier(m.key, idx, k, e.target.value)}
                          style={{
                            ...tinyInput,
                            background: editable ? '#fff' : '#F3F4F6',
                            color: editable ? '#111827' : '#6B7280',
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

function numericTierKeys(tiers) {
  if (!tiers || tiers.length === 0) return [];
  const skip = new Set(['grade']);
  const keys = new Set();
  for (const t of tiers) {
    for (const k of Object.keys(t)) {
      if (skip.has(k)) continue;
      if (typeof t[k] === 'boolean') continue;
      keys.add(k);
    }
  }
  return [...keys];
}

function prettyKey(k) {
  return k.replace(/_/g, ' ');
}

// Build a dropdown of the most recent N quarters (current + last N-1
// completed), newest first. The default selection is the most recently
// completed quarter — that's what FMXers almost always want for a QBR.
function recentQuarters(count = 8, today = new Date()) {
  const y = today.getFullYear();
  const currentQ = Math.floor(today.getMonth() / 3); // 0-indexed
  const out = [];
  // Most recently completed first, then walk back. We include the in-progress
  // quarter at the bottom of the dropdown for the rare case a FMXer wants it.
  for (let i = 1; i <= count; i++) {
    out.push(buildQuarter(y, currentQ - i));
  }
  out.push({ ...buildQuarter(y, currentQ), isCurrent: true });
  return out;
}

function buildQuarter(yearAnchor, qIdxRaw) {
  let qIdx = qIdxRaw;
  let year = yearAnchor;
  while (qIdx < 0) { qIdx += 4; year -= 1; }
  while (qIdx > 3) { qIdx -= 4; year += 1; }
  const startMonth = qIdx * 3;
  const start = new Date(year, startMonth, 1);
  const end = new Date(year, startMonth + 3, 0);
  return {
    key: `${year}-Q${qIdx + 1}`,
    label: `Q${qIdx + 1} ${year}`,
    periodStart: iso(start),
    periodEnd: iso(end),
    isCurrent: false,
  };
}

function iso(d) {
  return d.toISOString().slice(0, 10);
}

function formatRange(startIso, endIso) {
  if (!startIso || !endIso) return '';
  const fmt = d => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  return `${fmt(startIso)} – ${fmt(endIso)}`;
}

// --- presentational helpers ---

function ModuleChecklist({ label, modules, selected, onToggle }) {
  if (!modules || modules.length === 0) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 11, color: C.textMid, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {modules.map(m => {
          const on = selected.has(m.slug);
          return (
            <label key={m.slug} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '5px 10px', fontSize: 12,
              border: `1px solid ${on ? C.orange : C.border}`,
              background: on ? '#FFF6F0' : '#fff',
              borderRadius: 5, cursor: 'pointer',
            }}>
              <input type="checkbox" checked={on} onChange={() => onToggle(m.slug)} />
              <span style={{ fontWeight: 600, color: C.navy }}>{m.label}</span>
              <span style={{ fontSize: 10, color: C.textLight, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{m.slug}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: C.textMid, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 500, color: '#374151' }}>{label}</span>
      {children}
    </label>
  );
}

function Radio({ checked, onChange, label, sublabel }) {
  return (
    <label style={{
      flex: 1, padding: 12, borderRadius: 8,
      border: `1px solid ${checked ? C.orange : C.border}`,
      background: checked ? '#FFF5F0' : '#fff',
      cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'flex-start',
    }}>
      <input type="radio" checked={checked} onChange={onChange} style={{ marginTop: 3 }} />
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.navy }}>{label}</div>
        <div style={{ fontSize: 11, color: C.textMid, marginTop: 2 }}>{sublabel}</div>
      </div>
    </label>
  );
}

const input = {
  padding: '6px 9px', fontSize: 13, borderRadius: 5,
  border: `1px solid ${C.border}`, outline: 'none',
};
const card = {
  border: `1px solid ${C.border}`, borderRadius: 8,
  padding: 12, background: '#fff',
};
const tinyTh = {
  padding: '4px 6px', fontSize: 10, fontWeight: 600, color: C.textMid,
  textTransform: 'uppercase', letterSpacing: 0.3, textAlign: 'left',
  borderBottom: `1px solid ${C.border}`,
};
const tinyTd = { padding: '4px 6px', fontSize: 12 };
const tinyInput = {
  width: 70, padding: '3px 6px', fontSize: 12, borderRadius: 4,
  border: `1px solid ${C.border}`, outline: 'none',
};
const errBox = {
  padding: '8px 12px', borderRadius: 6, fontSize: 12,
  background: C.errBg, color: C.errText, border: `1px solid ${C.errBorder}`,
};
